import React, {useEffect, useState} from "react";
import {Button, Container, Row, Col} from "react-bootstrap";
import {useDispatch, useSelector} from "react-redux";
import {useParams, useHistory} from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import ClaimedGiftCards from "../components/ClaimedGiftCards";
import {
  getDashboard,
  regClaimOffer,
  registeredLogout,
} from "../actions/registeredUserAction";
import useIdleTimeout from "../components/useIdleTimeout";
import IdleOverLay from "../components/IdleOverlay";
import ClaimedGiftCardsPaged from "../components/ClaimedGiftCardsPaged";
import {REGISTERED_CLAIM_ACKNOWLEDGED} from "../constants/registeredUserActions";
import RegisteredClaimStatusOverlay from "../components/RegisteredClaimStatusOverlay";
import RegisteredPendingOfferTable from "../components/RegisteredPendingOfferTable";
import RegisteredFailedOfferTable from "../components/RegisteredFailedOfferTable";

const RegisteredHomeScreen = () => {

  const dispatch = useDispatch();
  const history = useHistory();

  // State variable for modal login overlay displaying messages to website visitors
  const [showClaimStatusOverlay, setShowClaimStatusOverlay] = useState(false);

  // Create state variable to show/hide the idle timeout overlay
  const [showIdleOverlay, setShowIdleOverlay] = useState(false);

  // Get the registered user data from the Redux store
  const regUserData = useSelector((state) => state.registeredUserDetails);
  const {offerLoading, offerError, newClaimSuccess, userDashboardLoading,
    userDashboardError, userData} = regUserData;

  // Destructure the userData object
  const {
    firstName, lastName, currentOffers, pendingOffers, failedOffers,
    claimedGiftCards, claimedLegacyGiftCards
  } = userData;

  // Shows the idle overlay warning the user that they will be logged out soon
  const showIdlePrompt = () => {
    setShowIdleOverlay(true);
  };

  // Logs the user out if they click "Log Out"
  const handleLogout = () => {
    setShowIdleOverlay(false); // Hide the idle overlay if it is showing
    dispatch(registeredLogout()); // Clear the Redux store
    sessionStorage.clear(); // Clears the session storage
    history.replace("/registeredlogin"); // Show the registered login screen
  };

  // Use the idle timeout hook to track user activity
  const {idle, idleTimer} = useIdleTimeout({
    promptBeforeIdle: showIdlePrompt,
    idleTime: process.env.REACT_APP_USER_IDLE_TIMEOUT_MINUTES, // User is logged out after many minutes of inactivity
    doWhenIdle: handleLogout,
  });

  // Closes the idle timeout overlay if the user clicks on the "Stay In" button
  const handleStay = () => {
    setShowIdleOverlay(false);
    idleTimer.reset();
  };

  // Callback to close the status overlay
  const handleClaimStatusOverlayClose = () => {
    setShowClaimStatusOverlay(false);
    // Reset the newClaimSuccess state in the Redux store
    dispatch({type: REGISTERED_CLAIM_ACKNOWLEDGED});
  };

  // Handle when the user clicks the "Claim" button
  const onClaimSubmit = (e) => {
    e.preventDefault();
    void dispatch(regClaimOffer());
  };

  // Handles the claim success popup
  useEffect(() => {
    if (newClaimSuccess) {
      setShowClaimStatusOverlay(true);
    }
  }, [newClaimSuccess]);

  // When the component first mounts, retrieve the user dashboard data
  useEffect(() => {
    void dispatch(getDashboard());
  }, []);

  return (
    <div className="gcotg-userhome-top-level">
      <Container fluid className="no-padding">
        <Row>
          <Col>
            <Header logoutHandler={handleLogout}/>
          </Col>
        </Row>
        <div className="gcotg-user-home-content">
          <Row>
            <Col className="userhome-main-content-col">
              <RegisteredClaimStatusOverlay
                show={showClaimStatusOverlay}
                handleClose={handleClaimStatusOverlayClose}
                orderData={userData}
              />
              {!userDashboardLoading && (
                <div>
                  <h2 className="userhome-greeting">
                    Welcome, {firstName} {lastName}!
                  </h2>
                </div>
              )}
              {(userDashboardLoading) && (
                <div>
                  <Loader/>
                  <strong>
                    Loading your dashboard. Please wait.
                  </strong>
                </div>
              )}
              {userDashboardError && (
                <div>
                  <p className="order-error-msg">Unable to load your dashboard. Please log out and try again later.</p>
                </div>
              )}
              {(offerLoading) && (
                <div>
                  <Loader/>
                  <strong>
                    Your offer is processing. This can take several minutes. Please be patient.
                  </strong>
                </div>
              )}
              {offerError && (
                <div>
                  <p className="order-error-msg">{offerError}</p>
                </div>
              )}
              <div>
                <div>
                  {showIdleOverlay && (
                    <IdleOverLay
                      show={showIdleOverlay}
                      handleStay={handleStay}
                      handleLogout={handleLogout}
                    />
                  )}
                </div>
                {currentOffers?.length === 0 &&
                  pendingOffers?.length === 0 &&
                  failedOffers?.length === 0 &&
                  (
                    <div>
                      <p className="no-offers-message">
                        No new offers available.<br/>
                        To see claimed gift cards, scroll <strong>&#8595;</strong>down<strong>&#8595;</strong>.
                      </p>
                    </div>
                  )}
                {currentOffers?.length > 0 && (
                  <div className="new-offers-outer-div">
                    <div className="new-offers-div">
                      <div className="gcotg-info-message">{userData?.message}</div>
                      {!offerError &&
                        currentOffers?.length > 0 && (
                          <p className="new-offers-message">
                            You have new gift card offers
                          </p>
                        )}
                      {!offerError &&
                        currentOffers?.length > 0 &&
                        currentOffers.map((giftCard) => (
                          <div key={giftCard.awardId}>
                            <h4 className="new-offers-item">
                              ${giftCard.price} {giftCard.promotionName} gift card
                            </h4>
                          </div>
                        ))}
                    </div>
                    <div>
                      {!offerLoading &&
                        !offerError &&
                        currentOffers?.length > 0 && (
                          <Button
                            variant="primary"
                            type="submit"
                            onClick={(e) => onClaimSubmit(e)}
                            className="gcotg-button"
                          >
                            CLICK HERE TO CLAIM
                          </Button>
                        )}
                    </div>
                  </div>
                )}
                <div>
                  {pendingOffers?.length ? (
                    <RegisteredPendingOfferTable userInfo={userData}/>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div>
                {failedOffers?.length ? (
                  <RegisteredFailedOfferTable userInfo={userData}/>
                ) : (
                  ""
                )}
              </div>
              {/*<div>*/}
              {/*  {claimedGiftCards?.length ? (*/}
              {/*    <ClaimedGiftCards userInfo={userData}/>*/}
              {/*  ) : (*/}
              {/*    ""*/}
              {/*  )}*/}
              {/*</div>*/}
              <div>
                {claimedGiftCards?.content.length
                + claimedLegacyGiftCards?.content.length ? (
                  <ClaimedGiftCardsPaged userInfo={userData}/>
                ) : (
                  ""
                )}
              </div>
            </Col>
          </Row>
        </div>
        <Row>
          <Col>
            <Footer userInfo={userData}/>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default RegisteredHomeScreen;
